import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, ButtonBase, CircularProgress, TextField, Typography } from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import DashboardCustomizeOutlinedIcon from '@mui/icons-material/DashboardCustomizeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Editor as SimpleWysiwygEditor, EditorProvider } from 'react-simple-wysiwyg';
import RichTextToolbar from '../_RichTextToolbar';

// The three ways the storefront stores the body of a dynamic page or a product
// container — the same three the old admin's content editor had:
//   wysiwyg  plain text, in the body_* columns           (is_via_file 0)
//   unlayer  designer, {lang}.html + {lang}.json files   (is_via_file 1)
//   html     own HTML, {lang}.html files                 (is_via_file 1, is_custom_html 1)
// This file holds what the two hosts (DynamicPageTab, the container dialog)
// share: the mode switch, the editor for the two text modes, and the
// load-state notice. The designer itself stays with each host.

const MODES = [
    { v: 'wysiwyg', k: 'ui_content_mode_text',     l: 'Text',          icon: <EditOutlinedIcon fontSize="small" /> },
    { v: 'unlayer', k: 'ui_content_mode_designer', l: 'Designer',      icon: <DashboardCustomizeOutlinedIcon fontSize="small" /> },
    { v: 'html',    k: 'ui_content_mode_html',     l: 'Own HTML',      icon: <CodeIcon fontSize="small" /> },
];

const trOf = (t) => (key, fallback) => (typeof t === 'function' ? t(key, fallback) : fallback);

/** Three-way switch between text, designer and own HTML. */
export const EditorModeSwitch = ({ value, onChange, t, disabled = false, busy = false }) => {
    const tr = trOf(t);
    return (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            {MODES.map((m) => {
                const active = m.v === value;
                return (
                    <ButtonBase
                        key={m.v}
                        disabled={disabled || busy}
                        onClick={() => { if (!active && onChange) onChange(m.v); }}
                        data-content-mode={m.v}
                        sx={{
                            py: 1, borderRadius: 1.5,
                            border: '1.5px solid',
                            borderColor: active ? 'primary.main' : 'divider',
                            backgroundColor: active ? 'rgba(25,118,210,0.08)' : 'background.paper',
                            color: active ? 'primary.main' : 'text.primary',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                            opacity: (disabled || busy) && !active ? 0.5 : 1,
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: 'primary.main' },
                        }}
                    >
                        {busy && active ? <CircularProgress size={16} /> : m.icon}
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{tr(m.k, m.l)}</Typography>
                    </ButtonBase>
                );
            })}
        </Box>
    );
};
EditorModeSwitch.propTypes = {
    value: PropTypes.oneOf(['wysiwyg', 'unlayer', 'html']),
    onChange: PropTypes.func,
    t: PropTypes.func,
    disabled: PropTypes.bool,
    busy: PropTypes.bool,
};

/**
 * Shown instead of the text editor while the stored text is being fetched, or
 * when it could not be fetched. The editor must not open empty in that case:
 * saving would replace the text on the shop by nothing.
 */
export const ContentBodyNotice = ({ status, onRetry, t }) => {
    const tr = trOf(t);
    if (status === 'loading') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 2, color: 'text.secondary' }}>
                <CircularProgress size={18} />
                <Typography variant="body2">{tr('ui_content_loading', 'Loading the current text…')}</Typography>
            </Box>
        );
    }
    return (
        <Alert
            severity="warning"
            data-content-body="error"
            action={onRetry ? <Button color="inherit" size="small" onClick={onRetry} sx={{ textTransform: 'none' }}>{tr('ui_content_retry', 'Try again')}</Button> : undefined}
        >
            {tr('ui_content_load_failed', 'The current text could not be loaded, so it cannot be edited here. The webshop may still need its update.')}
        </Alert>
    );
};
ContentBodyNotice.propTypes = { status: PropTypes.string, onRetry: PropTypes.func, t: PropTypes.func };

/**
 * Editor for the two text modes: a small rich-text editor ('wysiwyg') or a
 * plain source field ('html'). One language at a time — the host owns the
 * language tabs and passes `docKey` (which document + language this is).
 *
 * Keystrokes are buffered and handed up after a short pause, on blur and when
 * the document changes: the hosts keep their content in the big config state,
 * where every change re-runs the unsaved-changes comparison.
 */
export const TextBodyEditor = ({ mode, value, onChange, docKey, t, minHeight = 260 }) => {
    const tr = trOf(t);
    const [draft, setDraft] = useState(null);     // { key, val } while keystrokes are pending
    const pendingRef = useRef(null);              // { key, val, commit }
    const timerRef = useRef(null);
    const flush = () => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
        const p = pendingRef.current;
        if (p) {
            pendingRef.current = null;
            setDraft(null);
            p.commit(p.val);
        }
    };
    const flushRef = useRef(flush);
    flushRef.current = flush;
    // Hand over what is pending before another document takes this editor, and
    // on unmount. `commit` was captured with the keystroke, so it still writes
    // to the document that was being typed in.
    useEffect(() => { flushRef.current(); }, [docKey, mode]);
    useEffect(() => () => { flushRef.current(); }, []);

    const edit = (val) => {
        setDraft({ key: docKey, val });
        pendingRef.current = { key: docKey, val, commit: onChange };
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => flushRef.current(), 300);
    };
    const shown = draft && draft.key === docKey ? draft.val : (value || '');

    if (mode === 'html') {
        return (
            <TextField
                multiline fullWidth
                minRows={12} maxRows={28}
                value={shown}
                onChange={(e) => edit(e.target.value)}
                onBlur={flush}
                placeholder={tr('ui_content_html_placeholder', 'Paste or type your own HTML here.')}
                inputProps={{ spellCheck: false, 'data-content-editor': 'html' }}
                sx={{ '& textarea': { fontFamily: 'Consolas, Menlo, monospace', fontSize: '0.8rem', lineHeight: 1.5 } }}
            />
        );
    }
    return (
        <Box
            data-content-editor="wysiwyg"
            sx={{
                border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
                '& .rsw-editor':  { minHeight: minHeight + 40, border: 'none' },
                '& .rsw-toolbar': { borderBottom: '1px solid', borderColor: 'divider' },
                '& .rsw-ce':      { minHeight, padding: '12px 16px' },
            }}
        >
            <EditorProvider>
                <SimpleWysiwygEditor
                    value={shown}
                    onChange={(e) => edit(e.target.value)}
                    onBlur={flush}
                >
                    <RichTextToolbar t={t} />
                </SimpleWysiwygEditor>
            </EditorProvider>
        </Box>
    );
};
TextBodyEditor.propTypes = {
    mode: PropTypes.oneOf(['wysiwyg', 'html']).isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    docKey: PropTypes.string,
    t: PropTypes.func,
    minHeight: PropTypes.number,
};
